import { Box, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistry } from '../../store/RegistryStore';
import { addDays, date, DEMO_TODAY, money } from '../../lib/format';
import { useInvestorScope } from '../../store/selectors';
import { Card } from '../common';

interface Item {
  id: string;
  date: string;
  label: string;
  amount: number;
  direction: 'in' | 'out';
  to: string;
}

export default function UpcomingPayments() {
  const scope = useInvestorScope();
  const { state } = useRegistry();
  const navigate = useNavigate();
  const items = useMemo(() => {
    const horizon = addDays(DEMO_TODAY, 90);
    const list: Item[] = [];
    for (const call of scope.calls.filter((item) => item.status === 'ISSUED')) {
      for (const line of call.lines.filter(
        (item) => scope.holderIds.has(item.holderId) && item.status !== 'PAID',
      )) {
        list.push({
          id: `${call.id}-${line.holderId}`,
          date: call.dueDate,
          label: `${scope.funds.find((fund) => fund.id === call.fundId)?.code} capital call ${call.number}`,
          amount: line.amount - line.paid,
          direction: 'out',
          to: '/investor/payments',
        });
      }
    }
    for (const distribution of state.distributions.filter(
      (item) =>
        ['APPROVED', 'PENDING_APPROVAL', 'DRAFT'].includes(item.status) &&
        item.paymentDate <= horizon,
    )) {
      for (const line of distribution.lines.filter((item) => scope.holderIds.has(item.holderId))) {
        list.push({
          id: `${distribution.id}-${line.holderId}`,
          date: distribution.paymentDate,
          label: `${scope.funds.find((fund) => fund.id === distribution.fundId)?.code} distribution (estimated)`,
          amount: line.net,
          direction: 'in',
          to: '/investor/payments',
        });
      }
    }
    return list.sort((left, right) => left.date.localeCompare(right.date));
  }, [scope, state.distributions]);

  if (items.length === 0) {
    return (
      <Card>
        <Typography variant="body2" color="text.secondary">
          Nothing scheduled in the next 90 days.
        </Typography>
      </Card>
    );
  }
  return (
    <Card sx={{ p: 0 }}>
      {items.map((item, index) => (
        <Stack
          key={item.id}
          direction="row"
          spacing={2}
          alignItems="center"
          onClick={() => navigate(item.to)}
          sx={{
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            borderTop: index ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ width: 44, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1 }}>
              {date(item.date).split(' ')[0]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {date(item.date).split(' ')[1]}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {item.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: item.direction === 'in' ? 'success.main' : 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            {item.direction === 'in' ? '+' : '−'}
            {money(item.amount)}
          </Typography>
        </Stack>
      ))}
    </Card>
  );
}
